import React from "react";

import { Editor } from "@tinymce/tinymce-react";
import { Editor as IEditor } from "tinymce";

import { acceptableLanguages, spellcheckerRpcUrl } from 'utils/spellcheckLanguages';
import { ConnectedProps, connect } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import * as actions from 'modules/posts/actionCreator';
import { MENTIONS_MARK_HIGHLIGHT_STYLE } from "utils/generalConstants";
import { GIF_VISIBLE_OPTION, getEditorLength } from "utils/textEditorUtils";

interface IBasicTinyMceProps {
    placeholder?: string;
    editorRef: React.MutableRefObject<IEditor | null>;
    gifRef: React.MutableRefObject<any>;
    disabled?: boolean;
    value?: string;
    id: string;
    isGifsVisible: boolean;
    onChangeIsGifsVisible: (value: boolean) => void;
    onChange: (newText: string, event: IEditor) => void;
    initializeCallback?: () => void;
}

/**
 * A tiny mce text box with basic plugins
 */
const BasicTinyMceComponent: React.FunctionComponent<PropsWithRedux> = ({
    placeholder,
    commentMaxLength,
    editorRef,
    gifRef,
    defaultLcid,
    tenantGifsEnabled,
    value,
    disabled,
    id,
    isGifsVisible,
    onChangeIsGifsVisible,
    grabMentionUsers,
    onChange,
    initializeCallback
}) => {
    // A missing/zero limit must not lock the editor, so fall back to "no cap". Held in a
    // ref because the handlers below are registered once, when the editor initialises,
    // and would otherwise keep reading the limit from that first render.
    const maxLengthRef = React.useRef(0);
    maxLengthRef.current = commentMaxLength > 0 ? commentMaxLength : Number.MAX_SAFE_INTEGER;

    /** Characters that can still be added, accounting for text the caret would replace. */
    const remainingLength = (editor: IEditor) =>
        maxLengthRef.current - (getEditorLength(editor) - editor.selection.getContent({ format: "text" }).length);

    const setup = (editor: IEditor) => {
        editor.on("init", (e) =>{
            editorRef.current = editor;
            if(initializeCallback)
                initializeCallback();
        });

        // TinyMCE has no maxlength equivalent, so insertions are capped here. Only inputs
        // that add characters are checked: deletions, undo/redo and formatting must keep
        // working at the limit, or an over-length comment could not be edited down.
        // Pasting is left to PastePreProcess below, which trims it instead of refusing it.
        editor.on("beforeinput", (e: any) => {
            const inputType: string = e.inputType ?? "";

            if (!inputType.startsWith("insert") || inputType.startsWith("insertFromPaste") || inputType.startsWith("insertFromDrop"))
                return;

            // A newline or IME commit carries no data but still costs one character.
            const incoming = (e.data ?? " ").length;

            if (incoming > remainingLength(editor))
                e.preventDefault();
        });

        // powerpaste inserts content itself rather than through beforeinput. paste_as_text
        // is on, so the payload is plain text and can be trimmed down to what still fits.
        editor.on("PastePreProcess", (e: any) => {
            const remaining = remainingLength(editor);
            const pastedText = new DOMParser().parseFromString(e.content, "text/html").body.textContent ?? "";

            if (pastedText.length > remaining)
                e.content = remaining > 0 ? editor.dom.encode(pastedText.slice(0, remaining)) : "";
        });

        editor.options.register(GIF_VISIBLE_OPTION, {
            processor: 'boolean',
            default: isGifsVisible
        });

        if (tenantGifsEnabled) {
            editor.ui.registry.addToggleButton('gifs', {
                icon: 'embed',
                text: 'GIF',
                onAction: function (api) {
                    gifRef.current = api;
                    api.setActive(!api.isActive())
                    editor.options.set(GIF_VISIBLE_OPTION, !editor.options.get(GIF_VISIBLE_OPTION))

                    onChangeIsGifsVisible(editor.options.get(GIF_VISIBLE_OPTION));
                }
            });
        }

        const onAction = function (autocompleteApi, rng, value) {
            editor.selection.setRng(rng);
            editor.insertContent(`<mark data-user-id="${value.id}" contenteditable="false" style="${MENTIONS_MARK_HIGHLIGHT_STYLE}">@${value.name}</mark> `);
            autocompleteApi.hide();
        }

        editor.ui.registry.addAutocompleter('specialchars', {
            ch: '@',
            minChars: 0,
            columns: 1,
            highlightOn: ['name_text', 'email_text'],
            onAction: onAction,
            fetch: async function (pattern) {
                let results = await grabMentionUsers(pattern);

                const userData = results.map(u => ({
                    fullName: (u.firstName === "" ? "[Unknown]" : u.firstName + ' ' + u.lastName),
                    email: u.email, id: u.id, color: u.color, firstName: u.firstName, lastName: u.lastName, initials: u.firstName === "" || u.lastName === "" ? "Un" : u.firstName[0] + u.lastName[0]
                }));

                return userData.map(function (char) {
                    let colorForAvatar = char.color;
                    if (char.color !== null && char.color !== "")
                        colorForAvatar = colorForAvatar.substr(1);

                    return {
                        type: 'cardmenuitem',
                        value: ({ id: char.id, name: char.fullName === "[Unknown]" ? char.email : char.fullName }),
                        label: char.fullName,
                        items: [
                            {
                                type: 'cardcontainer',
                                direction: 'horizontal',
                                items: [
                                    {
                                        type: 'cardtext',
                                        text: char.initials,
                                        name: 'name_image',
                                        classes: ['mentions-icon-formatting', 'avatar-color-' + colorForAvatar]
                                    },
                                    {
                                        type: 'cardcontainer',
                                        direction: 'vertical',
                                        items: [
                                            {
                                                type: 'cardtext',
                                                text: char.fullName,
                                                name: 'name_text'
                                            },
                                            {
                                                type: 'cardtext',
                                                text: char.email,
                                                name: 'email_text',
                                                classes: ['mentions-email-formatting']
                                            }
                                        ]
                                    }]
                            }
                        ]
                    }
                }).slice(0, 6);
            }
        });
    }

    let spellcheckEnabled = true;
    let spellcheckLanguage = defaultLcid.substring(0, 2)?.toLocaleLowerCase() ?? "en";

    if (!acceptableLanguages.includes(spellcheckLanguage.toLocaleLowerCase())) {
        spellcheckLanguage = "en";
        spellcheckEnabled = false;
    }

    return (
        <Editor
            init={{
                object_resizing: false,
                image_title: true,
                menubar: false,
                branding: false,
                statusbar: false,
                paste_block_drop: true,
                powerpaste_block_drop: true,
                force_br_newlines: true,
                paste_as_text: true,
                placeholder: placeholder,
                gifvisible: isGifsVisible,
                plugins: ['tinymcespellchecker', 'powerpaste'],
                noneditable_noneditable_class: 'mceNonEditable',
                toolbar: "bold italic underline | gifs",
                spellchecker_rpc_url: spellcheckerRpcUrl,
                spellchecker_language: spellcheckLanguage,
                spellchecker_active: spellcheckEnabled,
                value: value,
                width: '100%',
                height: '210px',
                paddingtop: '10px',
                content_style: "body { min-height: 100px; font-family: Roboto, Helvetica, Arial, sans-serif; }" +
                    ".mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #aaaaaa }",
                setup,
            }}
            id={id}
            value={value}
            onEditorChange={onChange}
            disabled={disabled}
        />
    );
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IBasicTinyMceProps) => ({
        ...ownProps,
        commentMaxLength: state.settings.clientSettings.commentSystem.commentMaxLength,
        defaultLcid: state.settings.clientSettings.defaultLCID,
        tenantGifsEnabled: state.settings.clientSettings.commentSystem.gifSelectionEnabledForComments
    }),
    {
        grabMentionUsers: actions.getMentionsUsers
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;
export const BasicTinyMce = connector(BasicTinyMceComponent);
